import { ReactNode } from 'react';
import clsx from 'clsx';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

type ModalSize = 'lg' | '2xl' | '3xl';

interface ModalProps {
  title: string;
  onClose: () => void;
  size?: ModalSize;
  children: ReactNode;
}

const sizeClass: Record<ModalSize, string> = {
  lg: 'max-w-lg',
  '2xl': 'max-w-2xl',
  '3xl': 'max-w-3xl',
};

export const Modal = ({
  title,
  onClose,
  size = '2xl',
  children,
}: ModalProps) => {
  const { t } = useTranslation();

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="presentation"
    >
      <button
        type="button"
        aria-label={t('common.close')}
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
        className={clsx(
          'relative flex max-h-[min(90vh,800px)] w-full flex-col rounded-xl bg-white shadow-2xl',
          sizeClass[size]
        )}
      >
        <div className="flex shrink-0 items-center justify-between border-b border-gray-100 px-5 py-3">
          <h2 id="modal-title" className="text-lg font-semibold text-gray-900">
            {title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1.5 text-gray-500 transition-colors hover:bg-gray-100"
            aria-label={t('common.close')}
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="flex min-h-0 flex-1 flex-col">{children}</div>
      </div>
    </div>
  );
};

interface ModalBodyProps {
  children: ReactNode;
  className?: string;
}

export const ModalBody = ({ children, className }: ModalBodyProps) => (
  <div className={clsx('min-h-0 flex-1 overflow-y-auto px-5 py-4', className)}>
    {children}
  </div>
);

interface ModalFooterProps {
  children: ReactNode;
}

export const ModalFooter = ({ children }: ModalFooterProps) => (
  <div className="flex shrink-0 justify-end gap-3 border-t border-gray-100 bg-white px-5 py-3">
    {children}
  </div>
);
